import { CircleInfoIcon, ImageIcon, StackIcon } from "@xola/icons";
import { Breadcrumb, HeaderToolbar, Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { isUndefined } from "lodash";
import React, { ReactNode, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { PartnerPackageWithExpandedProducts } from "types";
import { NoTranslate } from "../../components/Common/NoTranslate";
import { SidebarTabs } from "../../components/Common/SidebarTabs";
import { ColumnLayout } from "../../components/Layout/ColumnLayout";
import { PackagePhotoList } from "../../components/Product/PackagePhotoList";
import { PartnerPackageDescriptiveContent } from "../../components/Product/PartnerPackages/DescriptiveContent/PartnerPackageDescriptiveContent";
import { useResource } from "../../hooks/useResource";
import { Page } from "../Page";
import { EditPartnerPackagePricingPage } from "@/components/Product/PartnerPackages/EditPartnerPackagePricingPage";

export const PartnerPackagePage = () => {
    const history = useHistory();
    const { id, section } = useParams<{ id?: ID; section?: string }>();
    const { data: pkg } = useResource<PartnerPackageWithExpandedProducts>(
        ["partnerPackages", id],
        { expand: "selectedProducts" },
        {
            enabled: !!id && id !== "new",
            staleTime: 1000, // Cache for 1s so that its always re-fetched when the page loads
            keepPreviousData: true,
        },
    );

    const tabs = [
        {
            id: "description",
            label: "Package Description",
            icon: <CircleInfoIcon size="large" />,
            description: "Important details that will be presented to your customer throughout the booking process.",
            content: <PartnerPackageDescriptiveContent pkg={pkg} />,
        },
        {
            id: "pricing",
            label: "Pricing",
            icon: <StackIcon size="large" />,
            condition: !!pkg,
            description: "Manage email and SMS notifications to your customers and staff",
            content: pkg && <EditPartnerPackagePricingPage pkg={pkg} />,
        },
        {
            id: "photos",
            label: "Photos",
            icon: <ImageIcon size="large" />,
            condition: !!pkg,
            description:
                "Set your primary photo which will be set as the image in the back office and in online checkout",
            content: pkg && <PackagePhotoList pkg={pkg} />,
        },
    ];

    const initialSection = section ?? tabs[0].id;
    const initialActiveIndex = tabs.findIndex((tab) => tab.id === section ?? initialSection);
    const [activeTabIndex, setActiveTabIndex] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    useEffect(() => {
        if (section) {
            const newIndex = tabs.findIndex((tab) => tab.id === section);
            if (newIndex >= 0 && activeTabIndex !== newIndex) {
                setActiveTabIndex(newIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    const breadcrumbs: Array<{ label: ReactNode; url?: string; tabIndex?: number }> = [
        { label: "Products" },
        { label: "Partner Packages", url: "/products/partnerPackages" },
        { label: pkg ? <NoTranslate>{pkg.name}</NoTranslate> : "New", tabIndex: 0 },
        { label: tabs[activeTabIndex].label, tabIndex: activeTabIndex },
    ];

    const handleBreadcrumbClick = (index: number, url?: string) => {
        if (url) {
            history.push(url);
        }

        setActiveTabIndex(index);
    };

    const handleActiveTabChange = (index: number) => {
        if (id) {
            const path = `/products/partnerPackages/${id}/${tabs[index].id}`;
            history.push(path);
        } else {
            setActiveTabIndex(index);
        }
    };

    return (
        <Page isDynamicTitle={!!pkg} title={pkg ? pkg.name : "Create Partner Package"}>
            <ColumnLayout>
                <HeaderToolbar classNames="justify-between">
                    <HeaderToolbar.Breadcrumb>
                        {breadcrumbs.map((breadcrumb) => (
                            <Breadcrumb.Item
                                key={breadcrumb.label}
                                onClick={() => handleBreadcrumbClick(breadcrumb.tabIndex ?? 0, breadcrumb.url)}
                            >
                                {breadcrumb.label}
                            </Breadcrumb.Item>
                        ))}
                    </HeaderToolbar.Breadcrumb>
                    <div id="header-toolbar-actions" />
                </HeaderToolbar>
                <ColumnLayout.Fill>
                    <SidebarTabs activeTab={activeTabIndex} onClick={handleActiveTabChange}>
                        {tabs.map((tab) => {
                            const isHidden = !isUndefined(tab.condition) && !tab.condition;
                            return (
                                <SidebarTabs.Tab
                                    key={tab.id}
                                    name={tab.label}
                                    icon={tab.icon}
                                    description={tab.description}
                                    className={clsx(isHidden && "hidden")}
                                >
                                    {tab.content ?? (
                                        <div className="p-4">
                                            <Skeleton />
                                        </div>
                                    )}
                                </SidebarTabs.Tab>
                            );
                        })}
                    </SidebarTabs>
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
